import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SearchIcon from '@client/assets/svgs/search-icon.svg';
import CrossIcon from '@client/assets/svgs/cross.svg';
import { Breakpoints, Colors, Index } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import { containerColorCss } from '@client/core/utils/colors';
import gsap from 'gsap';
import { getQueryFromLocation, useSearchQuery } from './QueryHook';
import Site from '@client/store/Site';
import Functions from '@client/style/Functions';
import useIsMobile from '@client/core/CustomHooks/useIsMobile';
import { focusFirstFocusable } from '@client/core/utils/a11y/focus-first-child';

const Container = styled.div`
	position: relative;
	display: contents;
	z-index: ${Index.mainMenuButton};

	${Functions.breakpoint(Breakpoints.tablet)} {
		position: relative;
		display: flex;
		align-items: start;
	}
`;

const InputContainer = styled.div`
	${containerColorCss(Colors.lightBackground)}
	${TextStyle.family('title')}
	${TextStyle.size('large', 'xlarge', 'laptop')}
	border-radius: 40px;
	overflow: hidden;
	display: flex;
	position: absolute;
	top: calc(0.8em * 2 + 1.8em + 20px);
	right: var(--gridGutter);
	width: calc(100vw - var(--gridGutter) * 2);

	${Functions.breakpoint(Breakpoints.tablet)} {
		position: relative;
		top: unset;
		right: unset;
		width: auto;
	}

	&:focus-within {
		outline: 1px auto;
	}
`;

const StyledInput = styled.input`
	height: unset;
	border: unset;
	border-radius: 40px;
	padding: 12px 24px;
	font: inherit;
	color: inherit;
	background: none;
	text-align: center;
	width: 100%;
	height: 2.4em;
	flex: 1 0 auto;

	&::placeholder {
		color: inherit;
		opacity: 0.5;
	}

	&:focus::placeholder {
		color: inherit;
		opacity: 0.5;
	}

	&:focus {
		outline: none;
	}

	${Functions.breakpoint(Breakpoints.tablet)} {
		height: unset;
		width: 14em;
		flex: 1 0 14em;
		text-align: left;
	}
`;

const Button = styled.button<{ $color?: string }>`
	display: flex;
	align-items: center;
	padding: 0.8em;
	cursor: pointer;

	&:focus-visible {
		outline: 1px auto;
	}

	svg {
		height: 1.8em;
		width: 1.8em;
		color: ${({ color }) => color || '#fff'};
	}
`;

export default function SearchInput({ color = undefined }: { color?: string }) {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const timeout = useRef<number>(undefined);
	const tween = useRef<gsap.core.Tween | null>(null);

	const [query, setQuery] = useSearchQuery();
	const [value, setValue] = useState('');

	const placeholder = Site.use(data => data.search.placeholderText);
	const isMobile = useIsMobile();

	const search = () => {
		window.clearTimeout(timeout.current);
		const val = value?.trim();
		if (val) {
			setQuery(val);
		}
	};

	const openSearch = () => {
		window.clearTimeout(timeout.current);
		setQuery('');
		inputRef.current?.focus();
	};

	const closeSearch = () => {
		window.clearTimeout(timeout.current);
		setValue('');
		setQuery(undefined);
	};

	/* set the default value from the URL query */
	useEffect(() => {
		setValue(getQueryFromLocation() || '');
	}, []);

	/* timeout to search when the input value changes */
	useEffect(() => {
		if (!value?.trim() && query !== undefined) setQuery('');
		timeout.current = window.setTimeout(() => {
			search();
		}, 500);

		return () => {
			window.clearTimeout(timeout.current);
		};
	}, [value]);

	/* animate the input container width when query changes */
	useEffect(() => {
		const props: gsap.TweenVars = {};
		if (isMobile) {
			containerRef.current?.style.removeProperty('width');
			props.height = () => (query !== undefined ? (inputRef.current?.offsetHeight ?? 250) : 0);
		} else {
			containerRef.current?.style.removeProperty('height');
			props.width = () => (query !== undefined ? (inputRef.current?.offsetWidth ?? 250) : 0);
		}

		tween.current = gsap.to(containerRef.current, {
			duration: 0.3,
			ease: 'power2.inOut',
			...props,
			onComplete: () => {
				if (query !== undefined) {
					inputRef.current?.focus();
					containerRef.current?.style.removeProperty('width');
					containerRef.current?.style.removeProperty('height');
				}
			},
		});

		return () => {
			tween.current?.kill();
		};
	}, [inputRef.current, containerRef.current, query, isMobile]);

	return (
		<Container>
			<InputContainer ref={containerRef}>
				<StyledInput
					ref={inputRef}
					type='search'
					id='search'
					placeholder={placeholder || 'Søg'}
					value={value}
					onChange={event => setValue(event.target.value)}
					tabIndex={query !== undefined ? 0 : -1}
					onFocus={() => setQuery(q => (q === undefined ? '' : q))}
					onBlur={() => {
						if (tween.current?.isActive()) return;
						if (!value?.trim() && query !== undefined) {
							closeSearch();
						}
					}}
					onKeyDown={ev => {
						if (ev.key === 'Enter') {
							ev.preventDefault();
							search();
							focusFirstFocusable(document.getElementById('site-search-content'));
						} else if (ev.key === 'Escape') {
							ev.preventDefault();
							closeSearch();
						}
					}}
				/>
			</InputContainer>
			<Button
				ref={buttonRef}
				$color={color}
				aria-pressed={query !== undefined}
				aria-label={'Åben/luk søgefelt'}
				title={query !== undefined ? 'Luk søgefelt' : 'Åben søgefelt'}
				onClick={() => {
					if (tween.current?.isActive()) return;
					// console.log('click', query !== undefined);
					if (query !== undefined) {
						closeSearch();
					} else {
						openSearch();
					}
				}}
			>
				{query !== undefined ? <CrossIcon /> : <SearchIcon />}
			</Button>
		</Container>
	);
}
